/* eslint-disable react-refresh/only-export-components */
import {
  AnchorHTMLAttributes,
  MouseEvent,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

type RouterValue = {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
};

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => `${window.location.pathname}${window.location.search}`);

  useEffect(() => {
    const onPopState = () => setPath(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      path,
      navigate: (to, options) => {
        if (options?.replace) window.history.replaceState(null, "", to);
        else window.history.pushState(null, "", to);
        setPath(`${window.location.pathname}${window.location.search}`);
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    }),
    [path]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const value = useContext(RouterContext);
  if (!value) throw new Error("RouterProvider is missing.");
  return value;
}

export function usePath() {
  return useRouter().path.split("?")[0];
}

export function useNavigate() {
  return useRouter().navigate;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };
type NavLinkProps = LinkProps & { end?: boolean };

export function Link({ to, onClick, children, ...props }: LinkProps) {
  const navigate = useNavigate();
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      props.target === "_blank"
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  }
  return <a {...props} href={to} onClick={handleClick}>{children}</a>;
}

export function NavLink({ to, children, end = false, className, ...props }: NavLinkProps) {
  const path = usePath();
  const active = end ? path === to : path === to || path.startsWith(`${to}/`);
  return <Link {...props} className={`${className ?? ""}${active ? " active" : ""}`.trim()} to={to}>{children}</Link>;
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}
